/**
 * Data layer for `day-parting-schedule-targeting`. Thin typed fetch wrappers over /api/v1/day-parting-schedule-targeting.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface DayPartingScheduleTargetingRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListDayPartingScheduleTargetingResponse {
	count: number;
	items: DayPartingScheduleTargetingRow[];
}

export interface CreateDayPartingScheduleTargetingBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/campaign-dayparting";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListDayPartingScheduleTargetingResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListDayPartingScheduleTargetingResponse>(res);
}

export async function create(body: CreateDayPartingScheduleTargetingBody): Promise<DayPartingScheduleTargetingRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<DayPartingScheduleTargetingRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const dayPartingScheduleTargetingQueryKeys = {
	all: ["day-parting-schedule-targeting"] as const,
	list: () => [...dayPartingScheduleTargetingQueryKeys.all] as const,
	detail: (id: string) => [...dayPartingScheduleTargetingQueryKeys.all, id] as const,
};
